import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import TagButton from "../TagButton";
import styles from "./ActiveFilters.module.scss";

import Icons from "@/components/icons";
import { Button } from "@/components/ui/v2/button";
import { Switch } from "@/components/ui/v2/switch";

type Filter = {
  key: string;
  label?: string;
  value: string | number;
  valueLabel?: string;
};

type ActiveFiltersProps = {
  addedFilters: Filter[];
  onRemoveFilter: (filter: Filter) => void;
  handleClearFilters: () => void;
  toggleFilter?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: string;
  };
};

const FilterToggle: React.FC<{
  toggleFilter: NonNullable<ActiveFiltersProps["toggleFilter"]>;
}> = ({ toggleFilter }) => (
  <div className={styles.toggleContainer}>
    <h5>{toggleFilter.label}</h5>
    <Switch
      checked={toggleFilter.checked}
      onCheckedChange={toggleFilter.onChange}
    />
  </div>
);

const FilterList = React.forwardRef<
  HTMLDivElement,
  {
    filters: Filter[];
    onRemove: (filter: Filter) => void;
      }
      >(({ filters, onRemove }, ref) => {
        if (filters.length === 0) return null;
        return (
          <div className={styles.filterList} ref={ref}>
            <div className={styles.tagButtons}>
              {filters.map((filter) => (
                <div key={`${filter.key}-${filter.value}`}>
                  <TagButton
                    label={`${filter.label}: ${filter.valueLabel ?? filter.value}`}
                    size="small"
                    onClick={() => onRemove(filter)}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      });

const ClearFiltersButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.clearButton}>
      <Button variant="link" size="sm" onClick={onClick}>
        <Icons.DeleteIcon fontSize={24} />
        {t("buttons.resetFilters")}
      </Button>
    </div>
  );
};

const ActiveFilters: React.FC<ActiveFiltersProps> = ({
  addedFilters,
  onRemoveFilter,
  handleClearFilters,
  toggleFilter,
}) => {
  const [rightFadeOpacity, setRightFadeOpacity] = useState(1);
  const [leftFadeOpacity, setLeftFadeOpacity] = useState(0);
  const filterListRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback((element: HTMLDivElement) => {
    const fadeDistance = 100; // pixels over which the fade happens
    const scrollRight =
      element.scrollWidth - element.scrollLeft - element.clientWidth;
    const scrollLeft = element.scrollLeft;

    // Calculate right fade opacity (1 when far from edge, 0 when at edge)
    const rightOpacity = Math.min(1, scrollRight / fadeDistance);
    setRightFadeOpacity(rightOpacity);

    // Calculate left fade opacity (1 when scrolled right, 0 when at left edge)
    const leftOpacity = Math.min(1, scrollLeft / fadeDistance);
    setLeftFadeOpacity(leftOpacity);
  }, []);

  useEffect(() => {
    const filterList = filterListRef.current;
    if (!filterList) return;

    const scrollHandler = (e: Event) =>
      handleScroll(e.target as HTMLDivElement);
    filterList.addEventListener("scroll", scrollHandler);
    handleScroll(filterList);

    return () => {
      filterList.removeEventListener("scroll", scrollHandler);
    };
  }, [handleScroll]);

  const hasFilters = addedFilters.length > 0;
  if (!hasFilters && !toggleFilter) return null;

  const rightFadeStyle = { opacity: rightFadeOpacity };
  const leftFadeStyle = { opacity: leftFadeOpacity };

  return (
    <div className={styles.container}>
      {toggleFilter && (
        <div className={styles.filterToggle}>
          <FilterToggle toggleFilter={toggleFilter} />
        </div>
      )}

      {hasFilters && (
        <>
          {toggleFilter && <div className={styles.rectangle} />}
          <div className={styles.activeFilterRow}>
            <div className={styles.filterContent}>
              <ClearFiltersButton onClick={handleClearFilters} />
              <div className={styles.filterListWrapper}>
                <div className={styles.leftFade} style={leftFadeStyle}></div>
                <FilterList
                  filters={addedFilters}
                  onRemove={onRemoveFilter}
                  ref={filterListRef}
                />
                <div className={styles.rightFade} style={rightFadeStyle}></div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ActiveFilters;
